import { useEffect, useState } from "react";
import { Pencil, Plus, Trash2 } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import { toast } from "@/components/ui/sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import AiWriter from "@/components/admin/AiWriter";

const EMPTY = { game: "League of Legends", event: "", opponent: "", score: "", status: "À VENIR", date: "", mvp: "", stream_url: "", opgg_url: "", match_datetime: "" };
const inputCls = "w-full rounded-lg border border-input bg-[#060B18] px-3 py-2 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-orange-500";

export default function MatchesTab() {
  const [matches, setMatches] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/matches").then((r) => setMatches(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await api.put(`/admin/matches/${editing}`, form);
        toast.success("Match mis à jour");
      } else {
        await api.post("/admin/matches", form);
        toast.success("Match ajouté");
      }
      setForm(EMPTY);
      setEditing(null);
      load();
    } catch (err) { toast.error(errMsg(err)); }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/admin/matches/${id}`);
      toast.success("Match supprimé");
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="grid gap-6" data-testid="matches-admin">
      <form onSubmit={submit} className="card-tactical grid gap-4 p-5" data-testid="admin-match-form">
        <p className="flex items-center gap-2 font-heading text-lg font-bold uppercase">
          {editing ? <Pencil className="h-5 w-5 text-orange-500" /> : <Plus className="h-5 w-5 text-orange-500" />}
          {editing ? "Modifier le match" : "Ajouter un match / résultat"}
        </p>
        <div className="grid gap-3 sm:grid-cols-3">
          <Select value={form.game} onValueChange={(v) => setForm({ ...form, game: v })}>
            <SelectTrigger data-testid="admin-match-game" className="bg-[#060B18]"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="League of Legends">League of Legends</SelectItem>
              <SelectItem value="Teamfight Tactics">Teamfight Tactics</SelectItem>
              <SelectItem value="Rainbow Six Siege">Rainbow Six Siege</SelectItem>
            </SelectContent>
          </Select>
          <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
            <SelectTrigger data-testid="admin-match-status" className="bg-[#060B18]"><SelectValue /></SelectTrigger>
            <SelectContent>
              {["À VENIR", "VICTOIRE", "DÉFAITE", "QUALIFIÉ"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
          <input placeholder="Événement (ex: Open Tour — Finale)" value={form.event} data-testid="admin-match-event"
            onChange={(e) => setForm({ ...form, event: e.target.value })} className={inputCls} required />
          <input placeholder="Adversaire" value={form.opponent}
            onChange={(e) => setForm({ ...form, opponent: e.target.value })} className={inputCls} required />
          <input placeholder="Score (ex: 2 - 1 ou Prochain match)" value={form.score}
            onChange={(e) => setForm({ ...form, score: e.target.value })} className={inputCls} required />
          <input placeholder="Date (ex: 25 Juillet 2026, 20:00)" value={form.date}
            onChange={(e) => setForm({ ...form, date: e.target.value })} className={inputCls} required />
          <input placeholder="MVP (optionnel)" value={form.mvp}
            onChange={(e) => setForm({ ...form, mvp: e.target.value })} className={inputCls} />
          <input placeholder="URL du stream (optionnel)" value={form.stream_url}
            onChange={(e) => setForm({ ...form, stream_url: e.target.value })} className={inputCls} />
          <input placeholder="Lien de l'équipe adverse — OP.GG, site, réseau... (optionnel)" value={form.opgg_url} data-testid="admin-match-opgg"
            onChange={(e) => setForm({ ...form, opgg_url: e.target.value })} className={inputCls} />
          <label className="text-xs font-bold uppercase tracking-widest text-slate-400">
            Date/heure précise (compte à rebours)
            <input type="datetime-local" value={form.match_datetime} data-testid="admin-match-datetime"
              onChange={(e) => setForm({ ...form, match_datetime: e.target.value })} className={inputCls + " mt-1"} />
          </label>
        </div>
        <div className="flex gap-3">
          <button type="submit" data-testid="admin-match-submit"
            className="rounded-lg bg-orange-500 px-5 py-2.5 text-sm font-bold uppercase text-[#060B18] transition-all hover:scale-[1.02] hover:bg-orange-400">
            {editing ? "Enregistrer" : "Ajouter"}
          </button>
          {editing && (
            <button type="button" onClick={() => { setEditing(null); setForm(EMPTY); }} data-testid="admin-match-cancel"
              className="rounded-lg border border-slate-600 px-5 py-2.5 text-sm font-bold uppercase text-slate-300">
              Annuler
            </button>
          )}
        </div>
      </form>

      <div className="grid gap-3">
        {matches.map((m) => (
          <div key={m.id} data-testid={`admin-match-row-${m.id}`} className="card-tactical flex flex-wrap items-center justify-between gap-3 p-4">
            <div>
              <span className="mr-2 rounded border border-orange-500/40 bg-orange-500/10 px-2 py-0.5 text-[11px] font-bold uppercase text-orange-400">{m.status}</span>
              <span className="font-heading text-lg font-bold uppercase">Emisphere vs {m.opponent}</span>
              <span className="ml-2 text-sm text-slate-400">{m.event} — {m.score} — {m.date}</span>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <AiWriter kind="recap" testid={`admin-match-recap-${m.id}`} buttonLabel="Récap IA"
                useLabel="Créer un brouillon d'actualité"
                context={{ game: m.game, event: m.event, opponent: m.opponent, score: m.score, mvp: m.mvp || "", date: m.date }}
                onUse={async ({ title, text }) => {
                  await api.post("/admin/news", {
                    title: title || `Récap : Emisphere vs ${m.opponent}`,
                    category: m.game,
                    date: m.date,
                    summary: text,
                    featured: false,
                    published: false,
                  });
                  toast.success("Brouillon d'actualité créé — retrouvez-le dans l'onglet Actus");
                }} />
              <button onClick={() => { setEditing(m.id); setForm({ game: m.game, event: m.event, opponent: m.opponent, score: m.score, status: m.status, date: m.date, mvp: m.mvp || "", stream_url: m.stream_url || "", opgg_url: m.opgg_url || "", match_datetime: m.match_datetime || "" }); }}
                data-testid={`admin-match-edit-${m.id}`}
                className="rounded-lg border border-orange-500/40 p-2 text-orange-400 hover:bg-orange-500/10"><Pencil className="h-4 w-4" /></button>
              <button onClick={() => remove(m.id)} data-testid={`admin-match-delete-${m.id}`}
                className="rounded-lg border border-red-500/40 p-2 text-red-400 hover:bg-red-500/10"><Trash2 className="h-4 w-4" /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
